import { useCallback, useMemo } from "react";
import { addDays, startOfDay } from "date-fns";

type DateLike = string | Date;
type CalendarDateInput = string | Date | number;

type UseStopoverTravelDatesAvailabilityParams = {
	departureCity?: string;
	destinationResort?: string;
	departureDate?: DateLike;
	returnDate?: DateLike;
};

const CALENDAR_MIN_OFFSET_DAYS = 2;
const CALENDAR_DEFAULT_MAX_OFFSET_DAYS = 340;

const RETURN_MIN_AFTER_DEPARTURE_DAYS = 3;
const RETURN_MAX_FROM_TODAY_DAYS = 350;

export const useStopoverTravelDatesAvailability = ({
	departureCity,
	destinationResort,
	departureDate,
	returnDate,
}: UseStopoverTravelDatesAvailabilityParams) => {
	const today = useMemo(() => startOfDay(new Date()), []);

	const minDate = useMemo(() => addDays(today, CALENDAR_MIN_OFFSET_DAYS).getTime(), [today]);

	const defaultMaxDate = useMemo(
		() => addDays(today, CALENDAR_DEFAULT_MAX_OFFSET_DAYS).getTime(),
		[today]
	);

	const maxReturnDateFromToday = useMemo(
		() => addDays(today, RETURN_MAX_FROM_TODAY_DAYS).getTime(),
		[today]
	);

	const maxDate = departureDate && !returnDate ? maxReturnDateFromToday : defaultMaxDate;

	const isDisabled = !departureCity && !destinationResort;

	const isDateDisabled = useCallback(
		(date?: CalendarDateInput) => {
			if (!date || isDisabled) {
				return true;
			}

			if (departureDate && !returnDate) {
				const departureTs = startOfDay(new Date(departureDate)).getTime();
				const dateToCheckTs = startOfDay(new Date(date)).getTime();

				const minReturnDateTs = addDays(
					startOfDay(new Date(departureDate)),
					RETURN_MIN_AFTER_DEPARTURE_DAYS
				).getTime();

				const isValidReturnDate =
					dateToCheckTs >= minReturnDateTs && dateToCheckTs <= maxReturnDateFromToday;

				const isValidAnteriorDepartureDate =
					dateToCheckTs >= minDate && dateToCheckTs <= departureTs;

				return !(isValidReturnDate || isValidAnteriorDepartureDate);
			}

			return false;
		},
		[departureDate, returnDate, isDisabled, maxReturnDateFromToday, minDate]
	);

	return {
		minDate,
		maxDate,
		isDisabled,
		hasError: false,
		isDateDisabled,
	};
};
